// src/components/Sidebar.tsx
// Collapsible sidebar navigation for the SGS Connect admin dashboard.
// Navy background with gold accents and smooth collapse animation.

import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Calendar,
  Mic2,
  Heart,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BookOpen,
  Book,
  Megaphone,
  ScanQrCode,
  MessageSquare,
  Tags,
  LayoutGrid,
  Activity,
  ShoppingBag,
  GraduationCap,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { toast } from 'sonner'
import LogoWhite from '@/assets/logo_white.png'

// ── Types ─────────────────────────────────────────────────────

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  badge?: number
}

// ── Nav config ────────────────────────────────────────────────

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/classes', icon: GraduationCap, label: 'Classes' },
  { to: '/members', icon: Users, label: 'Members' },
  { to: '/gratia-families', icon: LayoutGrid, label: 'Gratia Family' },
  { to: '/announcements', icon: Megaphone, label: 'Announcements' },
  { to: '/news', icon: BookOpen, label: 'Info Jemaat' },
  { to: '/blessing-market', icon: ShoppingBag, label: 'Blessing Market' },
  { to: '/events', icon: Calendar, label: 'Events' },
  { to: '/attendance', icon: ScanQrCode, label: 'Attendance' },
  { to: '/sermons', icon: Mic2, label: 'Sermons' },
  { to: '/prayers', icon: Heart, label: 'Prayers' },
  { to: '/testimonies', icon: MessageSquare, label: 'Testimonies' },
  { to: '/daily-verses', icon: Book, label: 'Daily Verses' },
  { to: '/our-services', icon: Activity, label: 'Our Service' },
  { to: '/event-types', icon: Tags, label: 'Event Types' },
  { to: '/settings', icon: Settings, label: 'Settings' },
]



// ── Component ─────────────────────────────────────────────────

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const { profile, signOut, authLoading } = useAuthStore()

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success('Signed out successfully')
    } catch {
      toast.error('Sign out failed. Please try again.')
    }
  }

  // Get initials for avatar fallback
  const initials = profile?.full_name
    ? profile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  return (
    <aside
      className={cn(
        'relative flex flex-col h-screen transition-all duration-300 ease-in-out flex-shrink-0',
        collapsed ? 'w-16' : 'w-60'
      )}
      style={{ background: 'linear-gradient(180deg, #1E3A5F 0%, #162d4a 100%)' }}
    >
      {/* ── Collapse toggle ────────────────────────────────── */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          'absolute -right-3 top-6 z-10',
          'w-6 h-6 rounded-full flex items-center justify-center',
          'transition-colors duration-150 shadow-md border',
          'hover:scale-110 active:scale-95'
        )}
        style={{
          background: '#D4A843',
          borderColor: '#b8902e',
          color: '#1A1A2E',
        }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>

      {/* ── Logo / Brand ────────────────────────────────────── */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-5 flex-shrink-0',
          'border-b',
          collapsed && 'justify-center px-2'
        )}
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        {/* Church icon */}
        <div
          className={cn(
            'flex items-center justify-center flex-shrink-0 overflow-hidden transition-all duration-300',
            collapsed ? 'w-10 h-10' : 'w-14 h-14'
          )}
        >
          <img src={LogoWhite} alt="SGS" className="w-full h-full object-contain" />
        </div>

        {/* Text — hidden when collapsed */}
        {!collapsed && (
          <div className="overflow-hidden">
            <div className="text-white font-bold text-sm leading-tight truncate">
              SGS Connect
            </div>
            <div
              className="text-xs font-medium truncate"
              style={{ color: 'rgba(212,168,67,0.85)', letterSpacing: '0.06em' }}
            >
              Admin
            </div>
          </div>
        )}
      </div>

      {/* ── Navigation ──────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          // Exact match for dashboard, prefix match for others
          const isActive =
            item.to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.to)

          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                'flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150',
                'group relative',
                collapsed ? 'justify-center px-0 py-3 mx-0' : 'px-3 py-2.5',
                isActive
                  ? 'text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              )}
              style={
                isActive
                  ? {
                      background: 'rgba(212,168,67,0.2)',
                      borderLeft: collapsed ? 'none' : '3px solid #D4A843',
                      paddingLeft: collapsed ? undefined : '10px',
                    }
                  : undefined
              }
              title={collapsed ? item.label : undefined}
            >
              {/* Active indicator (left border alternative for collapsed) */}
              {isActive && collapsed && (
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full"
                  style={{ background: '#D4A843' }}
                />
              )}

              <Icon
                className={cn(
                  'flex-shrink-0 transition-colors',
                  collapsed ? 'w-5 h-5' : 'w-4 h-4',
                  isActive ? 'text-amber-400' : 'text-white/60 group-hover:text-white'
                )}
                style={isActive ? { color: '#D4A843' } : undefined}
              />

              {!collapsed && (
                <span className="truncate">{item.label}</span>
              )}

              {/* Badge */}
              {!collapsed && item.badge != null && item.badge > 0 && (
                <span
                  className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center"
                  style={{ background: '#D4A843', color: '#1A1A2E' }}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}

              {/* Tooltip for collapsed mode */}
              {collapsed && (
                <div
                  className={cn(
                    'absolute left-full ml-2 px-2 py-1 rounded text-xs font-medium',
                    'opacity-0 group-hover:opacity-100 pointer-events-none',
                    'transition-opacity duration-150 whitespace-nowrap z-50',
                    'shadow-lg'
                  )}
                  style={{ background: '#1E3A5F', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  {item.label}
                </div>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* ── Divider ─────────────────────────────────────────── */}
      <div className="mx-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }} />

      {/* ── User profile + sign out ──────────────────────────── */}
      <div className={cn('p-3', collapsed && 'px-2')}>
        <div
          className={cn(
            'flex items-center rounded-lg transition-colors duration-150',
            collapsed ? 'flex-col gap-2 p-2' : 'gap-3 p-2',
            'hover:bg-white/5'
          )}
        >
          {/* Avatar */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ background: '#D4A843' }}
          >
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.full_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs font-bold" style={{ color: '#1A1A2E' }}>
                {initials}
              </span>
            )}
          </div>

          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {profile?.full_name ?? 'Loading...'}
              </p>
              <p className="text-xs capitalize" style={{ color: 'rgba(212,168,67,0.8)' }}>
                {profile?.role ?? ''}
              </p>
            </div>
          )}

          {/* Sign out button */}
          <button
            onClick={handleSignOut}
            disabled={authLoading}
            className={cn(
              'flex-shrink-0 p-1.5 rounded-md transition-all duration-150',
              'text-white/40 hover:text-red-400 hover:bg-red-400/10',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Sign out"
            aria-label="Sign out"
          >
            {authLoading ? (
              <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </aside>
  )
}
